import {
  COMMENT_MAX_LENGTH,
  type DocumentComment,
  decodeMentions,
  encodeMentions,
  type Mention,
  type Mentionable,
  mentionQueryAt,
  splitMentions,
} from "@cva/shared";
import { Check, CornerDownRight, MessageSquare, Pencil, RotateCcw, Trash2 } from "lucide-react";
import { Fragment, type ReactNode, useMemo, useRef, useState } from "react";
import { cn, formatDuration, formatRelative } from "../lib/utils";
import { Avatar } from "./app-shell";

/**
 * Pieces of the collaboration UI shared by the editor and the public share
 * page (docs/15-zusammenarbeit.md): comment threads and pins.
 */

export interface CommentThread {
  root: DocumentComment;
  replies: DocumentComment[];
  /** 1-based, in the order the threads were started (pins show it). */
  number: number;
}

/** Threads in the order they were started; replies below their root. */
export function buildThreads(comments: readonly DocumentComment[]): CommentThread[] {
  const roots = comments.filter((comment) => !comment.parentId);
  return roots.map((root, index) => ({
    root,
    replies: comments.filter((comment) => comment.parentId === root.id),
    number: index + 1,
  }));
}

/** Number of open comment threads as a small pill. */
export function CommentCount({ count, className }: { count: number; className?: string }) {
  if (count <= 0) return null;
  return (
    <span
      title={`${count} offene${count === 1 ? "r" : ""} Kommentar${count === 1 ? "" : "e"}`}
      className={cn(
        "inline-flex items-center gap-1 rounded-full bg-white/90 px-1.5 py-0.5 text-[11px] font-medium text-zinc-700 shadow-sm [&_svg]:size-3",
        className,
      )}
    >
      <MessageSquare /> {count}
    </span>
  );
}

/** A comment's text with @mentions highlighted. */
export function MentionText({ text }: { text: string }) {
  return (
    <>
      {splitMentions(text).map((part, index) =>
        "mention" in part ? (
          // biome-ignore lint/suspicious/noArrayIndexKey: parts of one immutable text
          <span key={index} className="rounded bg-brand-50 px-0.5 font-medium text-brand-700">
            @{part.mention.name}
          </span>
        ) : (
          // biome-ignore lint/suspicious/noArrayIndexKey: parts of one immutable text
          <Fragment key={index}>{part.text}</Fragment>
        ),
      )}
    </>
  );
}

/** Members matching what was typed after @: names starting with it first. */
function matchMembers(members: readonly Mentionable[], query: string): Mentionable[] {
  const q = query.trim().toLowerCase();
  const score = (member: Mentionable) => {
    const name = member.name.toLowerCase();
    if (!q || name.startsWith(q)) return 0;
    if (name.split(/\s+/).some((part) => part.startsWith(q))) return 1;
    if (name.includes(q) || member.email.toLowerCase().startsWith(q)) return 2;
    return -1;
  };
  return members
    .map((member) => ({ member, score: score(member) }))
    .filter((entry) => entry.score >= 0)
    .sort((a, b) => a.score - b.score || a.member.name.localeCompare(b.member.name, "de"))
    .slice(0, 6)
    .map((entry) => entry.member);
}

/**
 * Text field for a new comment or reply (⌘↵ sends). With `mentionables`, typing
 * @ offers the organization's members; the field shows "@Name", the stored text
 * carries a token per mention (see @cva/shared, domain/mentions.ts).
 */
export function CommentComposer({
  onSubmit,
  placeholder = "Kommentar schreiben …",
  autoFocus,
  submitLabel = "Senden",
  onCancel,
  initial = "",
  extra,
  mentionables,
}: {
  onSubmit: (body: string) => Promise<unknown>;
  placeholder?: string;
  autoFocus?: boolean;
  submitLabel?: string;
  onCancel?: () => void;
  initial?: string;
  /** Above the buttons (e.g. the guest's name). */
  extra?: ReactNode;
  /** Members that can be mentioned (none: no @ autocomplete, e.g. guests). */
  mentionables?: readonly Mentionable[] | undefined;
}) {
  const decoded = useMemo(() => decodeMentions(initial), [initial]);
  const [body, setBody] = useState(decoded.text);
  const [picked, setPicked] = useState<Mention[]>(decoded.mentions);
  const [busy, setBusy] = useState(false);
  const [mention, setMention] = useState<{ start: number; query: string } | null>(null);
  const [highlight, setHighlight] = useState(0);
  const field = useRef<HTMLTextAreaElement>(null);
  const suggestions = useMemo(
    () => (mention && mentionables?.length ? matchMembers(mentionables, mention.query) : []),
    [mention, mentionables],
  );
  const track = (element: HTMLTextAreaElement) => {
    if (!mentionables?.length) return;
    const next = mentionQueryAt(element.value, element.selectionStart ?? element.value.length);
    setMention((current) =>
      current?.start === next?.start && current?.query === next?.query ? current : next,
    );
  };
  const pick = (member: Mentionable) => {
    if (!mention) return;
    const element = field.current;
    const caret = element?.selectionStart ?? body.length;
    const insert = `@${member.name} `;
    const position = mention.start + insert.length;
    setBody(body.slice(0, mention.start) + insert + body.slice(caret));
    setPicked((list) => [...list.filter((entry) => entry.id !== member.id), member]);
    setMention(null);
    setHighlight(0);
    requestAnimationFrame(() => {
      element?.focus();
      element?.setSelectionRange(position, position);
    });
  };
  const submit = async () => {
    const text = body.trim();
    if (!text || busy) return;
    // Picked ones first (they win for equal names), then anyone typed by hand.
    const known = [
      ...picked,
      ...(mentionables ?? []).filter((member) => !picked.some((entry) => entry.id === member.id)),
    ];
    setBusy(true);
    try {
      await onSubmit(encodeMentions(text, known));
      setBody("");
      setPicked([]);
    } catch {
      // The caller shows the error; the text stays for another try.
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-2">
      <textarea
        ref={field}
        // biome-ignore lint/a11y/noAutofocus: opened on purpose to type right away
        autoFocus={autoFocus}
        value={body}
        maxLength={COMMENT_MAX_LENGTH}
        onChange={(event) => {
          setBody(event.target.value);
          setHighlight(0);
          track(event.target);
        }}
        onSelect={(event) => track(event.currentTarget)}
        onBlur={() => setTimeout(() => setMention(null), 150)}
        onKeyDown={(event) => {
          if (suggestions.length > 0) {
            const choose =
              (event.key === "Enter" && !event.metaKey && !event.ctrlKey) || event.key === "Tab";
            if (event.key === "ArrowDown" || event.key === "ArrowUp") {
              event.preventDefault();
              event.stopPropagation();
              const step = event.key === "ArrowDown" ? 1 : -1;
              setHighlight((index) => (index + step + suggestions.length) % suggestions.length);
              return;
            }
            if (choose || event.key === "Escape") {
              event.preventDefault();
              event.stopPropagation();
              if (choose) pick(suggestions[Math.min(highlight, suggestions.length - 1)]!);
              else setMention(null);
              return;
            }
          }
          if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            void submit();
          }
          if (event.key === "Escape" && onCancel) {
            event.preventDefault();
            onCancel();
          }
          // Keep editor shortcuts (delete, arrows …) out of the text field.
          event.stopPropagation();
        }}
        placeholder={placeholder}
        rows={2}
        className="block min-h-16 w-full resize-y rounded-lg border border-zinc-200 px-2.5 py-2 text-sm focus:border-brand-500 focus:outline-none"
      />
      {suggestions.length > 0 ? (
        <div
          role="listbox"
          aria-label="Mitglied erwähnen"
          className="overflow-hidden rounded-lg border border-zinc-200 bg-white py-1 shadow-lg"
        >
          {suggestions.map((member, index) => (
            <button
              key={member.id}
              type="button"
              role="option"
              aria-selected={index === highlight}
              // Keep the focus (and the caret) in the text field.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => pick(member)}
              onMouseEnter={() => setHighlight(index)}
              className={cn(
                "flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-sm",
                index === highlight ? "bg-brand-50 text-brand-800" : "text-zinc-700",
              )}
            >
              <Avatar name={member.name} className="size-5 text-[9px]" />
              <span className="min-w-0 flex-1 truncate font-medium">{member.name}</span>
              <span className="max-w-[45%] truncate text-[11px] text-zinc-400">{member.email}</span>
            </button>
          ))}
        </div>
      ) : null}
      {extra}
      <div className="flex justify-end gap-2">
        {onCancel ? (
          <button
            type="button"
            onClick={onCancel}
            className="h-7 rounded-md px-2.5 text-xs font-medium text-zinc-600 hover:bg-zinc-100"
          >
            Abbrechen
          </button>
        ) : null}
        <button
          type="button"
          disabled={!body.trim() || busy}
          onClick={() => void submit()}
          className="h-7 rounded-md bg-brand-600 px-3 text-xs font-semibold text-white hover:bg-brand-700 disabled:bg-zinc-200 disabled:text-zinc-500"
        >
          {submitLabel}
        </button>
      </div>
    </div>
  );
}

function CommentBody({ comment, actions }: { comment: DocumentComment; actions?: ReactNode }) {
  return (
    <div className="group/comment flex gap-2">
      <Avatar name={comment.author.name} className="size-6 text-[10px]" />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-1.5">
          <span className="truncate text-xs font-semibold text-zinc-900">
            {comment.author.name}
          </span>
          {comment.author.guest ? (
            <span className="rounded bg-zinc-100 px-1 text-[10px] text-zinc-500">Gast</span>
          ) : null}
          <span className="shrink-0 text-[11px] text-zinc-400">
            {formatRelative(comment.createdAt)}
          </span>
          <span className="ml-auto flex shrink-0 gap-0.5 opacity-0 transition group-hover/comment:opacity-100 focus-within:opacity-100">
            {actions}
          </span>
        </div>
        <p className="mt-0.5 text-sm break-words whitespace-pre-wrap text-zinc-800">
          <MentionText text={comment.body} />
        </p>
      </div>
    </div>
  );
}

const actionButton =
  "rounded p-0.5 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 [&_svg]:size-3.5";

export interface ThreadActions {
  reply?: (parentId: string, body: string) => Promise<unknown>;
  /** Resolve / reopen the thread. */
  resolve?: (rootId: string, resolved: boolean) => Promise<unknown>;
  edit?: (commentId: string, body: string) => Promise<unknown>;
  /** Only offered where `canRemove` allows it. */
  remove?: (commentId: string) => Promise<unknown>;
  canRemove?: (comment: DocumentComment) => boolean;
}

/** One thread: first comment, replies, reply field and actions. */
export function CommentThreadView({
  thread,
  actions,
  active,
  onActivate,
  label,
  mentionables,
}: {
  thread: CommentThread;
  actions: ThreadActions;
  active?: boolean;
  onActivate?: () => void;
  /** Where the comment sits (page, time …). */
  label?: ReactNode;
  /** Members for @mentions in replies and edits. */
  mentionables?: readonly Mentionable[] | undefined;
}) {
  const [replying, setReplying] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const resolved = Boolean(thread.root.resolvedAt);
  const tools = (comment: DocumentComment) => (
    <>
      {actions.edit && comment.mine ? (
        <button
          type="button"
          title="Bearbeiten"
          className={actionButton}
          onClick={() => setEditing(comment.id)}
        >
          <Pencil />
        </button>
      ) : null}
      {actions.remove && actions.canRemove?.(comment) ? (
        <button
          type="button"
          title={comment.parentId ? "Antwort löschen" : "Kommentar samt Antworten löschen"}
          className={cn(actionButton, "hover:text-red-600")}
          onClick={() => void actions.remove!(comment.id)}
        >
          <Trash2 />
        </button>
      ) : null}
    </>
  );
  const body = (comment: DocumentComment) =>
    editing === comment.id && actions.edit ? (
      <CommentComposer
        autoFocus
        initial={comment.body}
        mentionables={mentionables}
        submitLabel="Speichern"
        onCancel={() => setEditing(null)}
        onSubmit={async (text) => {
          await actions.edit!(comment.id, text);
          setEditing(null);
        }}
      />
    ) : (
      <CommentBody comment={comment} actions={tools(comment)} />
    );
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: the whole card selects the thread; its buttons stay keyboard reachable
    <div
      onClick={onActivate}
      className={cn(
        "rounded-xl border bg-white p-3 transition",
        active ? "border-brand-400 ring-2 ring-brand-400/20" : "border-zinc-200",
        resolved && !active && "opacity-60",
      )}
    >
      <div className="mb-2 flex items-center gap-2">
        <span
          className={cn(
            "flex size-5 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold",
            resolved ? "bg-zinc-200 text-zinc-600" : "bg-brand-600 text-white",
          )}
        >
          {thread.number}
        </span>
        <span className="min-w-0 flex-1 truncate text-[11px] text-zinc-500">{label}</span>
        {actions.resolve ? (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              void actions.resolve!(thread.root.id, !resolved);
            }}
            title={resolved ? "Wieder öffnen" : "Als erledigt markieren"}
            className={cn(
              "inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium [&_svg]:size-3.5",
              resolved ? "text-zinc-600 hover:bg-zinc-100" : "text-emerald-700 hover:bg-emerald-50",
            )}
          >
            {resolved ? <RotateCcw /> : <Check />}
            {resolved ? "Öffnen" : "Erledigt"}
          </button>
        ) : resolved ? (
          <span className="text-[11px] text-emerald-700">Erledigt</span>
        ) : null}
      </div>
      <div className="space-y-3">
        {body(thread.root)}
        {thread.replies.map((reply) => (
          <div key={reply.id} className="border-l-2 border-zinc-100 pl-3">
            {body(reply)}
          </div>
        ))}
      </div>
      {actions.reply ? (
        replying ? (
          <div className="mt-3">
            <CommentComposer
              autoFocus
              placeholder="Antworten …"
              mentionables={mentionables}
              submitLabel="Antworten"
              onCancel={() => setReplying(false)}
              onSubmit={async (text) => {
                await actions.reply!(thread.root.id, text);
                setReplying(false);
              }}
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              setReplying(true);
            }}
            className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-brand-700 hover:underline [&_svg]:size-3.5"
          >
            <CornerDownRight /> Antworten
          </button>
        )
      ) : null}
    </div>
  );
}

/** "Seite 2 · 0:14" – where a thread sits. */
export function threadLabel(
  comment: DocumentComment,
  pages: readonly { id: string }[],
  video: boolean,
): string {
  const parts: string[] = [];
  if (video && comment.timeMs !== null) parts.push(formatDuration(comment.timeMs));
  if (!video && pages.length > 1 && comment.pageId) {
    const index = pages.findIndex((page) => page.id === comment.pageId);
    if (index >= 0) parts.push(`Seite ${index + 1}`);
  }
  if (comment.x === null && !(video && comment.timeMs !== null)) parts.push("Allgemein");
  return parts.join(" · ");
}

/** A numbered pin on the page (document px); `scale` = screen px per document px. */
export function CommentPin({
  x,
  y,
  number,
  scale,
  active,
  resolved,
  draft,
  onClick,
}: {
  x: number;
  y: number;
  number?: number;
  scale: number;
  active?: boolean;
  resolved?: boolean;
  draft?: boolean;
  onClick?: () => void;
}) {
  const size = 26 / scale;
  return (
    <button
      type="button"
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => {
        event.stopPropagation();
        onClick?.();
      }}
      title={draft ? "Neuer Kommentar" : `Kommentar ${number}`}
      className={cn(
        "absolute flex items-center justify-center rounded-full rounded-bl-none border-white font-semibold shadow-md transition-transform",
        draft
          ? "animate-pulse bg-brand-600 text-white"
          : resolved
            ? "bg-zinc-400 text-white"
            : "bg-brand-600 text-white",
        active && "ring-brand-300",
      )}
      style={{
        left: x,
        top: y - size,
        width: size,
        height: size,
        fontSize: 12 / scale,
        borderWidth: 2 / scale,
        boxShadow: active ? `0 0 0 ${4 / scale}px rgb(36 87 235 / 0.35)` : undefined,
      }}
    >
      {draft ? "+" : number}
    </button>
  );
}
